import type { ConstantsAdminTeamStruct } from "@gosaas/types";
import { IconSearch, IconUsersGroup } from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useStaffGuard } from "@/components/admin/staff-guard";
import { Loader } from "@/components/Loader";
import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { useFetchAdminTeams } from "@/components/queries/admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { adminListPageCount } from "../users/index";

function TeamRow({ team }: { team: ConstantsAdminTeamStruct }) {
	return (
		<TableRow>
			<TableCell>
				<Link
					to="/admin/teams/$teamId"
					params={{ teamId: team.id ?? "" }}
					className="flex items-center gap-2 hover:underline"
				>
					<span className="text-lg">
						{team.emoji ? decodeURIComponent(team.emoji) : "💭"}
					</span>
					<span className="font-medium">{team.name}</span>
				</Link>
			</TableCell>
			<TableCell className="capitalize">{team.type}</TableCell>
			<TableCell>{team.member_count ?? 0}</TableCell>
			<TableCell>
				{team.plan_name ? (
					<div className="flex items-center gap-1.5">
						<span>{team.plan_name}</span>
						{team.subscription_status && (
							<Badge
								variant={
									team.subscription_status === "active" ||
									team.subscription_status === "trialing"
										? "secondary"
										: "outline"
								}
							>
								{team.subscription_status}
							</Badge>
						)}
					</div>
				) : (
					"—"
				)}
			</TableCell>
			<TableCell className="text-muted-foreground">
				{team.created_at
					? new Date(team.created_at).toLocaleDateString("en-US", {
							month: "short",
							day: "numeric",
							year: "numeric",
						})
					: "—"}
			</TableCell>
		</TableRow>
	);
}

export function AdminTeams() {
	const { isStaff, isLoading } = useStaffGuard();
	const [searchInput, setSearchInput] = useState("");
	const [query, setQuery] = useState("");
	const [page, setPage] = useState(1);
	const { teamList, isLoadingTeams } = useFetchAdminTeams(query, page);

	if (isLoading || !isStaff) {
		return <Loader />;
	}

	const total = teamList?.total ?? 0;
	const pageSize = teamList?.page_size ?? 25;
	const pageCount = adminListPageCount(total, pageSize);

	const submitSearch = (e: React.FormEvent) => {
		e.preventDefault();
		setQuery(searchInput.trim());
		setPage(1);
	};

	return (
		<PageLayout>
			<PageHeader
				breadcrumbItems={[
					{ title: "Admin", href: "/admin" },
					{ title: "Teams", href: "/admin/teams" },
				]}
				title="Teams"
				description="Search and inspect teams across the platform"
				icon={<IconUsersGroup className="size-6 text-primary" aria-hidden />}
			/>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
						<div>
							<CardTitle>All Teams</CardTitle>
							<CardDescription className="mt-1">
								{total} team{total === 1 ? "" : "s"}
							</CardDescription>
						</div>
						<form onSubmit={submitSearch} className="flex items-center gap-2">
							<Input
								placeholder="Search by name or email…"
								value={searchInput}
								onChange={(e) => setSearchInput(e.target.value)}
								className="md:w-72"
							/>
							<Button type="submit" variant="outline" size="icon">
								<IconSearch className="size-4" aria-hidden />
							</Button>
						</form>
					</div>
				</CardHeader>
				<CardContent className="pt-0">
					{isLoadingTeams ? (
						<Loader />
					) : (
						<>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Team</TableHead>
										<TableHead>Type</TableHead>
										<TableHead>Members</TableHead>
										<TableHead>Plan</TableHead>
										<TableHead>Created</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{(teamList?.teams ?? []).length === 0 && (
										<TableRow>
											<TableCell
												colSpan={5}
												className="text-center text-muted-foreground py-8"
											>
												No teams found.
											</TableCell>
										</TableRow>
									)}
									{(teamList?.teams ?? []).map((team) => (
										<TeamRow key={team.id} team={team} />
									))}
								</TableBody>
							</Table>
							<div className="flex items-center justify-between pt-4">
								<p className="text-sm text-muted-foreground">
									Page {page} of {pageCount}
								</p>
								<div className="flex gap-2">
									<Button
										variant="outline"
										size="sm"
										disabled={page <= 1}
										onClick={() => setPage((p) => Math.max(1, p - 1))}
									>
										Previous
									</Button>
									<Button
										variant="outline"
										size="sm"
										disabled={page >= pageCount}
										onClick={() => setPage((p) => p + 1)}
									>
										Next
									</Button>
								</div>
							</div>
						</>
					)}
				</CardContent>
			</Card>
		</PageLayout>
	);
}

/* istanbul ignore start -- TanStack Router file route registration */
export const Route = createFileRoute("/_authorized/admin/teams/")({
	component: AdminTeams,
});
/* istanbul ignore end */
